import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Banner,
    BannerImage,
    BannerImageContainer,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    BoundedContent,
    Li,
    Ol,
} from '@vp/swan';
import { sanitiseLocale } from '../../../../i18n/config';
import VideoControls from './VideoControls';
import type { Locale } from '../../../../i18n/config';

const getVideoCaptionsSrc = (locale: Locale = 'en-US'): string => {
    const cloudinaryBaseUrl = 'https://cms.cloudinary.vpsvc.com/raw/upload/vista-logos-pages';
    const captionFilePath =
        'logomaker-gen-ai/feature-banners/logomaker-gen-ai-how-it-works-captions.vtt';
    return `${cloudinaryBaseUrl}/${locale}/${captionFilePath}`;
};

const FeatureBannerHowItWorks = () => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [isCaptioning, setIsCaptioning] = useState(false);

    const handleVideoLoadedData = () => {
        // Fix for Safari, which doesn't honour the `<track>` default prop on unset or set to {false}
        if (videoRef.current?.textTracks[0]) {
            videoRef.current.textTracks[0].mode = 'disabled';
        }
    };

    const handlePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    };

    const handleMuteToggle = () => {
        setIsMuted(!isMuted);
    };

    const handleCaptioningToggle = () => {
        const textTracks = videoRef.current?.textTracks;
        if (!textTracks) return;

        const textTrack = textTracks[0];

        if (!textTrack) return;

        if (textTrack.mode === 'showing') {
            textTrack.mode = 'disabled';
        } else {
            textTrack.mode = 'showing';
        }

        setIsCaptioning(!isCaptioning);
    };

    const cloudinaryUrl = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Video.cloudinaryVideo.distributionUrl',
    );
    const videoPath = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Video.cloudinaryVideo.publicIdTemplate',
    );
    const videoSrc = `${cloudinaryUrl}video/upload/f_auto,q_auto/${videoPath}`;

    return (
        <BoundedContent
            as="section"
            backgroundColor="accent"
            id="how-it-works"
            paddingX={{ xs: 0, sm: 3, md: 4, lg: 8, xl: 10 }}
            paddingY={{ xs: 6, sm: 6, md: 6, lg: 8, xl: 9 }}
        >
            <Banner backgroundColor="accent">
                <BannerImageContainer>
                    <BannerImage>
                        <video
                            ref={videoRef}
                            poster={t(
                                'Logomaker GenAi Landing Page > Feature Banner How It Works > Video > Poster Image.src',
                            )}
                            preload="metadata"
                            playsInline
                            loop
                            autoPlay
                            muted={isMuted}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            onLoadedData={handleVideoLoadedData}
                            crossOrigin="anonymous"
                        >
                            <source src={videoSrc} type="video/mp4" />
                            <track
                                kind="captions"
                                srcLang="en"
                                label="English"
                                src={getVideoCaptionsSrc(locale)}
                            />
                        </video>
                    </BannerImage>
                    <VideoControls
                        isMuted={isMuted}
                        isPlaying={isPlaying}
                        isCaptioning={isCaptioning}
                        onMuteToggle={handleMuteToggle}
                        onPlayPause={handlePlayPause}
                        onCaptioningToggle={handleCaptioningToggle}
                    />
                </BannerImageContainer>
                <BannerTextContainer>
                    <BannerText marginLeft={0} textAlign="left">
                        <BannerTitle as="h2" fontSkin="title-section">
                            {t(
                                'Logomaker GenAi Landing Page > Feature Banner How It Works > Title.value',
                            )}
                        </BannerTitle>
                        <Ol skin="standard" start={1} paddingTop={5} fontSkin="body-standard-bold">
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 1.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 2.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 3.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 4.value',
                                )}
                            </Li>
                        </Ol>
                    </BannerText>
                </BannerTextContainer>
            </Banner>
        </BoundedContent>
    );
};
export default FeatureBannerHowItWorks;
